import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useIssueItemListScreen } from './hooks/useIssueItemListScreen'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'

const SEARCH_FIELD_OPTIONS = [
  { value: 'voucherNo', label: 'Voucher No' },
  { value: 'item', label: 'Item' },
  { value: 'branch', label: 'Branch' },
]

const DATE_PERIOD_OPTIONS = [
  { value: 'Period', label: 'Period' },
  { value: 'Current Year', label: 'Current Year' },
  { value: 'Current Month', label: 'Current Month' },
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
]

export default function IssueItemListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useIssueItemListScreen()

  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Issue Item
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant font-medium">
            Search, filter and manage issued inventory items for students.
          </p>
        </div>

        {state.fromStudent && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admission/students/list')}
              className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high cursor-pointer shadow-xs"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to Students
            </button>
          </div>
        )}
      </section>

      {/* ── Shared Filters ─────────────────────────────────────────────────── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        showStatus={false}
        showSearchButtons={true}
        companyFilter={state.companyFilter}
        setCompanyFilter={actions.setCompanyFilter}
        companyOptions={state.options.institutes}
        companyLabel="Institute"
        branchFilter={state.branchFilter}
        setBranchFilter={actions.setBranchFilter}
        branchOptions={state.options.branches}
        departmentFilter={state.departmentFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        departmentOptions={state.options.departments}
        voucherNoFilter={state.voucherNoFilter}
        setVoucherNoFilter={actions.setVoucherNoFilter}
        periodFilter={state.datePreset}
        setPeriodFilter={actions.handlePeriodChange}
        periodOptions={DATE_PERIOD_OPTIONS}
        periodLabel="Date: Period"
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        itemFilter={state.itemFilter}
        setItemFilter={actions.setItemFilter}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
        resetLabel="Show All"
        visibleAccordionFilters={[
          'company',
          'branch',
          'department',
          'voucherNo',
          'period',
          'fromDate',
          'toDate',
          'item',
        ]}
        tools={[
          { key: 'export', label: state.isExporting ? 'Exporting...' : 'Export Excel', icon: 'download' },
          { key: 'print', label: 'Print List', icon: 'print' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') actions.handleExport()
          else if (key === 'print') actions.handlePrint()
        }}
      />

      {/* ── Data Table Card ────────────────────────────────────────────────── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Table Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">inventory_2</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              List of Issued Items
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-on-surface-variant">
              {state.totalCount} {state.totalCount === 1 ? 'Record' : 'Records'}
            </span>
            <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
              <span>Show:</span>
              <select
                value={state.pageSize}
                onChange={(e) => {
                  actions.setPageSize(Number(e.target.value))
                  actions.setPage(1)
                }}
                className="h-8 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2 text-xs font-bold text-on-surface outline-none cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                <th className="px-5 py-3.5">vDate</th>
                <th className="px-5 py-3.5">vNo</th>
                <th className="px-5 py-3.5">Branch</th>
                <th className="px-5 py-3.5">Item</th>
                <th className="px-5 py-3.5 text-right">Qty</th>
                <th className="px-5 py-3.5 text-center">Student</th>
                <th className="w-24 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    Loading issued items...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/40">
                      inbox
                    </span>
                    <p className="text-sm font-medium text-on-surface-variant">
                      No issued items match the current filters.
                    </p>
                    <button
                      type="button"
                      onClick={actions.resetFilters}
                      className="mt-3 inline-flex items-center gap-1 rounded-xl bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
                    >
                      Show All
                    </button>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-surface-container-low/40 cursor-pointer"
                    onClick={() => actions.handleEdit(row.id)}
                  >
                    {/* vDate */}
                    <td className="px-5 py-3.5 text-sm text-on-surface whitespace-nowrap">
                      {formatDate(row.voucherDate)}
                    </td>

                    {/* vNo */}
                    <td className="px-5 py-3.5 font-mono text-sm font-bold text-primary">
                      {row.voucherNo}
                    </td>

                    {/* Branch */}
                    <td className="px-5 py-3.5 text-sm text-on-surface">{row.branch}</td>

                    {/* Item */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-on-surface">{row.item}</span>
                      </div>
                    </td>

                    {/* Qty */}
                    <td className="px-5 py-3.5 text-right font-semibold tabular-nums text-on-surface">
                      {row.quantity}
                    </td>

                    {/* Student Count Badge */}
                    <td className="px-5 py-3.5 text-center">
                      <span className="inline-flex items-center rounded-lg bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary font-mono">
                        {row.studentCount}
                      </span>
                    </td>

                    {/* Actions Column (Click-propagation guarded) */}
                    <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                        items={[
                          { key: 'edit', label: 'Edit', icon: 'edit' },
                          { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') actions.handleEdit(row.id)
                          else if (key === 'delete') actions.handleDelete(row.id, row.voucherNo)
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Shared Pagination ───────────────────────────────── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
